/**
 * BusinessTesoreria — cuentas por pagar con Mateo (lenguaje natural).
 *
 * El dueno u operador escribe "pagale 2.3 millones a Ferreteria El Tornillo el
 * 15 de julio, factura FE-123" -> Mateo lo estructura (preview) -> confirma ->
 * queda en el libro de AP con aging (vencidas / esta semana / este mes).
 * SOLO CONTABILIDAD: marcar pagada no mueve dinero — los pagos reales viven en
 * Pagos y nomina (con SMS del dueno). Visual: .tesoreria-ola glass Ola A (chrome only; API Mateo intacta).
 */
import { useCallback, useEffect, useState } from 'react';
import { mateoCapture, mateoSaveEntries, mateoListEntries, mateoMarkPaid, mateoResumen } from '../../lib/cerebro';

const fmtMonto = (n, moneda) =>
  moneda === 'USD'
    ? `$${Number(n || 0).toLocaleString('en-US')} USD`
    : `$${Math.round(Number(n || 0)).toLocaleString('es-CO')} COP`;

// timeZone UTC: 'vence' es fecha-sin-hora guardada a medianoche UTC; formatearla
// en hora Bogota (UTC-5) la retrocederia un dia (15 jul -> "14 jul").
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Sin fecha');

const EJEMPLO = 'Ej: pagale 2.3 millones a Ferreteria El Tornillo el 15 de julio, factura FE-123. Y 450 dolares a AWS el 1 de agosto.';

export default function BusinessTesoreria() {
  const [texto, setTexto] = useState('');
  const [preview, setPreview] = useState(null);   // { entradas, advertencias, textoOriginal }
  const [capturando, setCapturando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [entradas, setEntradas] = useState([]);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [pagandoId, setPagandoId] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([mateoResumen(), mateoListEntries('por_pagar')]);
      if (r?.sinEmpresa) { setError('Tu usuario aun no tiene una empresa vinculada.'); return; }
      setResumen(r);
      setEntradas(l?.entradas || []);
      setError('');
    } catch (err) {
      if (err?.response?.status === 403) setError('FlowexPay Business esta en piloto privado.');
      else setError('No pude cargar tus cuentas por pagar. Intenta de nuevo.');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const capturar = async () => {
    const t = texto.trim();
    if (!t || capturando) return;
    setCapturando(true);
    setOkMsg('');
    setError('');
    try {
      const result = await mateoCapture(t);
      if (result?.sinEmpresa) { setError('Tu usuario aun no tiene una empresa vinculada.'); return; }
      if (!result?.entradas?.length) {
        setError('Mateo no encontro pagos en el texto. Dile a quien, cuanto y (si quieres) cuando.');
        return;
      }
      setPreview(result);
    } catch (err) {
      if (err?.response?.status === 429) setError('Muchas capturas seguidas. Espera un momento.');
      else if (err?.response?.status === 403) setError('FlowexPay Business esta en piloto privado.');
      else setError('Mateo no pudo procesar el texto. Intenta de nuevo.');
    } finally {
      setCapturando(false);
    }
  };

  const confirmar = async () => {
    if (!preview || guardando) return;
    setGuardando(true);
    try {
      const r = await mateoSaveEntries(preview.entradas, preview.textoOriginal);
      setOkMsg(`Listo: ${r?.guardadas || 0} cuenta(s) por pagar guardada(s).`);
      setPreview(null);
      setTexto('');
      await cargar();
    } catch {
      setError('No se pudieron guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const pagar = async (id) => {
    if (pagandoId) return;
    setPagandoId(id);
    try {
      await mateoMarkPaid(id);
      await cargar();
    } finally {
      setPagandoId(null);
    }
  };

  const tiles = resumen ? [
    { label: 'Vencidas', n: resumen.vencidas?.length || 0, tone: 'bad' },
    { label: 'Esta semana', n: resumen.estaSemana?.length || 0, tone: 'warn' },
    { label: 'Este mes', n: resumen.esteMes?.length || 0, tone: 'info' },
    { label: 'Sin fecha', n: resumen.sinFecha?.length || 0, tone: 'muted' },
  ] : [];

  return (
    <div className="tesoreria-ola">
      <header className="tesoreria-ola__hero">
        <p className="tesoreria-ola__eyebrow">Tesorería</p>
        <h1 className="tesoreria-ola__title">Tus cuentas por pagar, en tus palabras</h1>
        <p className="tesoreria-ola__sub">
          Escribele a Mateo lo que debes pagar como se lo dirias a tu contador. El lo organiza,
          detecta duplicados y te avisa que vence primero.
        </p>
      </header>

      {error && <div className="tesoreria-ola__alert tesoreria-ola__alert--bad">{error}</div>}
      {okMsg && <div className="tesoreria-ola__alert tesoreria-ola__alert--good">{okMsg}</div>}

      <section className="tesoreria-ola__panel">
        <div className="tesoreria-ola__panel-head">
          <span className="tesoreria-ola__dot" aria-hidden="true" />
          <h2 className="tesoreria-ola__h2">Dictale tus pagos a Mateo</h2>
        </div>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={EJEMPLO}
          rows={3}
          className="tesoreria-ola__textarea"
        />
        <div className="tesoreria-ola__row">
          <p className="tesoreria-ola__hint">Mateo estructura el texto; tu confirmas antes de guardar. No mueve dinero.</p>
          <button
            type="button"
            disabled={capturando || !texto.trim()}
            onClick={capturar}
            className="tesoreria-ola__btn tesoreria-ola__btn--primary"
          >
            {capturando ? 'Mateo esta leyendo...' : 'Organizar con Mateo'}
          </button>
        </div>

        {preview && (
          <div className="tesoreria-ola__preview">
            <div className="tesoreria-ola__preview-label">Esto entendio Mateo — revisa y confirma</div>
            <div className="tesoreria-ola__list">
              {preview.entradas.map((e, i) => (
                <div key={i} className="tesoreria-ola__item">
                  <div>
                    <div className="tesoreria-ola__item-title">{e.proveedor}{e.factura ? <span className="tesoreria-ola__muted"> · {e.factura}</span> : null}</div>
                    <div className="tesoreria-ola__item-meta">{fmtFecha(e.vence)}{e.concepto ? ` · ${e.concepto}` : ''}</div>
                  </div>
                  <div className="tesoreria-ola__monto">{fmtMonto(e.monto, e.moneda)}</div>
                </div>
              ))}
            </div>
            {preview.advertencias?.length > 0 && (
              <div className="tesoreria-ola__alert tesoreria-ola__alert--warn">
                {preview.advertencias.map((a, i) => <div key={i}>{a}</div>)}
              </div>
            )}
            <div className="tesoreria-ola__actions">
              <button type="button" disabled={guardando} onClick={confirmar} className="tesoreria-ola__btn tesoreria-ola__btn--primary">
                {guardando ? 'Guardando...' : 'Confirmar y guardar'}
              </button>
              <button type="button" disabled={guardando} onClick={() => setPreview(null)} className="tesoreria-ola__btn tesoreria-ola__btn--ghost">
                Descartar
              </button>
            </div>
          </div>
        )}
      </section>

      {resumen && (
        <>
          <div className="tesoreria-ola__tiles">
            {tiles.map((t) => (
              <div key={t.label} className={`tesoreria-ola__tile tesoreria-ola__tile--${t.tone}`}>
                <div className="tesoreria-ola__tile-n">{t.n}</div>
                <div className="tesoreria-ola__tile-l">{t.label}</div>
              </div>
            ))}
          </div>
          {(resumen.totales?.COP > 0 || resumen.totales?.USD > 0) && (
            <p className="tesoreria-ola__total">
              Total por pagar: <strong>{fmtMonto(resumen.totales.COP, 'COP')}</strong>
              {resumen.totales.USD > 0 && <> + <strong>{fmtMonto(resumen.totales.USD, 'USD')}</strong></>}
            </p>
          )}
        </>
      )}

      <section className="tesoreria-ola__panel">
        <h2 className="tesoreria-ola__h2">Por pagar{resumen?.empresa ? ` — ${resumen.empresa}` : ''}</h2>
        {entradas.length === 0 ? (
          <p className="tesoreria-ola__empty">Nada pendiente. Dictale tus pagos a Mateo arriba.</p>
        ) : (
          <div className="tesoreria-ola__list">
            {entradas.map((e) => {
              // Comparacion por fecha de calendario (no por instante): vence es
              // medianoche UTC y compararla contra "ahora" la venceria un dia antes.
              const vencida = e.vence && new Date(e.vence).toISOString().slice(0, 10) < new Date().toLocaleDateString('en-CA');
              return (
                <div key={e._id} className="tesoreria-ola__item">
                  <div>
                    <div className="tesoreria-ola__item-title">{e.proveedor}{e.factura ? <span className="tesoreria-ola__muted"> · {e.factura}</span> : null}</div>
                    <div className={`tesoreria-ola__item-meta${vencida ? ' is-bad' : ''}`}>
                      {vencida ? 'Vencio ' : 'Vence '}{fmtFecha(e.vence).toLowerCase()}{e.concepto ? ` · ${e.concepto}` : ''}
                    </div>
                  </div>
                  <div className="tesoreria-ola__item-right">
                    <div className="tesoreria-ola__monto">{fmtMonto(e.monto, e.moneda)}</div>
                    <button
                      type="button"
                      disabled={pagandoId === e._id}
                      onClick={() => pagar(e._id)}
                      className="tesoreria-ola__chip"
                    >
                      {pagandoId === e._id ? '...' : 'Marcar pagada'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="tesoreria-ola__foot">Marcar pagada es contable: no mueve dinero. Los pagos reales viven en Pagos y nomina, con tu codigo SMS.</p>
      </section>

      <div className="tesoreria-ola__eta">
        Proximamente: tu caja rinde hasta el dia del pago (escalera de liquidez) — llega con la activacion de inversiones
      </div>
    </div>
  );
}
